/* ============================================================
 * Cart Drawer — Widgets abonnement Verlet
 * - <cart-subscription-line> : abo individuel par ligne
 * - <cart-subscription-all>  : "Tout abonner" global
 * Backend : Appstle (selling_plan_id natifs Shopify).
 * Endpoint utilise : POST {routes.cart_change_url} avec
 *   {line, quantity, selling_plan, sections, sections_url}.
 * ============================================================ */

(function () {
  const CART_DRAWER_SECTIONS = [
    { id: '#cart-counter', section: 'cart-counter', selector: '#shopify-section-cart-counter' },
    { id: '#CartDrawer-Body', section: 'cart-drawer', selector: '#shopify-section-cart-drawer #CartDrawer-Body' }
  ];

  function safeParseJSON(value, fallback) {
    try {
      return JSON.parse(value);
    } catch (e) {
      return fallback;
    }
  }

  function getCartChangeURL() {
    return (window.routes && window.routes.cart_change_url) || '/cart/change.js';
  }

  function getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  function renderCartDrawerSections(parsedState) {
    CART_DRAWER_SECTIONS.forEach(section => {
      const target = document.querySelector(section.id);
      if (!target || !parsedState.sections || !parsedState.sections[section.section]) return;
      target.innerHTML = getSectionInnerHTML(parsedState.sections[section.section], section.selector);
    });

    if (typeof window.updateFreeShipping === 'function') {
      window.updateFreeShipping();
    }
  }

  /**
   * Change le selling_plan d'une ligne du panier.
   * sellingPlanId : id numerique ou '' pour repasser en achat unique.
   */
  function updateLineSellingPlan(lineIndex, quantity, sellingPlanId) {
    const body = JSON.stringify({
      line: parseInt(lineIndex, 10),
      quantity: parseInt(quantity, 10),
      selling_plan: sellingPlanId === '' || sellingPlanId == null ? '' : String(sellingPlanId),
      sections: CART_DRAWER_SECTIONS.map(s => s.section),
      sections_url: window.location.pathname
    });

    const config = (typeof fetchConfig === 'function')
      ? fetchConfig()
      : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' } };

    return fetch(getCartChangeURL(), { ...config, body })
      .then(response => response.text())
      .then(text => JSON.parse(text));
  }

  /* ============================================================
   * <cart-subscription-line>
   * Pattern Magic Spoon : 1 CTA "S'ABONNER" en pill plein largeur
   * en mode non-abo, remplace par un select pill plein largeur en
   * mode abo. Pas de panneau, pas de confirmation : direct.
   * ============================================================ */
  class CartSubscriptionLine extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;

      this.lineIndex = this.dataset.lineIndex;
      this.lineQuantity = this.dataset.lineQuantity;
      this.defaultPlanId = this.dataset.defaultPlanId;

      const cta = this.querySelector('[data-action="subscribe"]');
      if (cta) {
        cta.addEventListener('click', () => {
          if (!this.defaultPlanId) return;
          this.applyPlan(this.defaultPlanId);
        });
      }

      const select = this.querySelector('[data-select]');
      if (select) {
        select.addEventListener('change', event => {
          this.applyPlan(event.target.value);
        });
      }
    }

    applyPlan(sellingPlanId) {
      this.classList.add('is-loading');
      const cartContainer = document.querySelector('cart-drawer');
      if (cartContainer) cartContainer.classList.add('is-loading');

      return updateLineSellingPlan(this.lineIndex, this.lineQuantity, sellingPlanId)
        .then(parsedState => {
          renderCartDrawerSections(parsedState);
        })
        .catch(err => {
          console.error('[cart-subscription] line update failed', err);
        })
        .finally(() => {
          this.classList.remove('is-loading');
          if (cartContainer) cartContainer.classList.remove('is-loading');
        });
    }
  }

  if (!customElements.get('cart-subscription-line')) {
    customElements.define('cart-subscription-line', CartSubscriptionLine);
  }

  /* ============================================================
   * <cart-subscription-all>
   * 1 CTA "S'abonner" qui applique la frequence par defaut de
   * chaque ligne eligible non-abonnee (data-default-plan-id pose
   * sur <cart-subscription-line>). Aucun selecteur de frequence :
   * comportement identique au CTA individuel.
   * ============================================================ */
  class CartSubscriptionAll extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;

      this.cta = this.querySelector('[data-action="subscribe-all"]');
      this.feedback = this.querySelector('[data-feedback]');

      if (this.cta) {
        this.cta.addEventListener('click', this.onConfirm.bind(this));
      }
    }

    getEligibleLines() {
      return Array.from(document.querySelectorAll('cart-subscription-line'))
        .filter(line => !line.dataset.currentPlanId || line.dataset.currentPlanId === '');
    }

    setFeedback(message, isError) {
      if (!this.feedback) return;
      if (!message) {
        this.feedback.hidden = true;
        this.feedback.textContent = '';
        this.feedback.classList.remove('is-error');
        return;
      }
      this.feedback.hidden = false;
      this.feedback.textContent = message;
      this.feedback.classList.toggle('is-error', !!isError);
    }

    onConfirm() {
      const lines = this.getEligibleLines();
      if (lines.length === 0) return;

      const snapshot = lines.map(line => ({
        lineIndex: line.dataset.lineIndex,
        lineQuantity: line.dataset.lineQuantity,
        sellingPlanId: line.dataset.defaultPlanId
      })).filter(item => item.sellingPlanId);

      if (snapshot.length === 0) {
        this.setFeedback("Aucun produit eligible.", true);
        return;
      }

      this.classList.add('is-loading');
      if (this.cta) this.cta.disabled = true;
      this.setFeedback('', false);

      const cartContainer = document.querySelector('cart-drawer');
      if (cartContainer) cartContainer.classList.add('is-loading');

      /**
       * Application en sequence (await) pour eviter les races
       * sur le cart state. Indexes traites du plus grand au plus
       * petit pour qu'un eventuel decalage ne casse pas la chaine.
       */
      const ordered = [...snapshot].sort((a, b) => parseInt(b.lineIndex, 10) - parseInt(a.lineIndex, 10));

      const promise = ordered.reduce((chain, item) => {
        return chain.then(() => updateLineSellingPlan(item.lineIndex, item.lineQuantity, item.sellingPlanId));
      }, Promise.resolve());

      promise
        .then(parsedState => {
          if (parsedState && parsedState.sections) {
            renderCartDrawerSections(parsedState);
          }
        })
        .catch(err => {
          console.error('[cart-subscription] subscribe-all failed', err);
          this.setFeedback("Une erreur est survenue. Reessayez.", true);
        })
        .finally(() => {
          this.classList.remove('is-loading');
          if (this.cta) this.cta.disabled = false;
          if (cartContainer) cartContainer.classList.remove('is-loading');
        });
    }
  }

  if (!customElements.get('cart-subscription-all')) {
    customElements.define('cart-subscription-all', CartSubscriptionAll);
  }

  /* ============================================================
   * Utils
   * ============================================================ */
  function escapeAttr(s) {
    return String(s).replace(/[&<>"']/g, ch => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[ch]));
  }
  function escapeHtml(s) { return escapeAttr(s); }
})();
